import React from 'react';
import { CircleHelp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';

function formatEstimateNumber(value) {
  if (!Number.isFinite(value)) return null;
  return Math.round(value).toLocaleString();
}

function formatEstimateRange(low, high) {
  const formattedLow = formatEstimateNumber(low);
  const formattedHigh = formatEstimateNumber(high);

  if (formattedLow && formattedHigh) {
    return `${formattedLow}–${formattedHigh}`;
  }
  return formattedLow || formattedHigh || null;
}

export default function ProposalDeliveryEstimateCard({
  deliveryEstimate = null,
  className = '',
}) {
  if (!deliveryEstimate) return null;

  const impressionsRange = formatEstimateRange(
    deliveryEstimate.estimatedImpressionsLow,
    deliveryEstimate.estimatedImpressionsHigh,
  );
  const reachRange = formatEstimateRange(
    deliveryEstimate.estimatedReachLow,
    deliveryEstimate.estimatedReachHigh,
  );

  if (!impressionsRange && !reachRange && !deliveryEstimate.disclaimerText) {
    return null;
  }

  return (
    <Card className={`border-slate-200 shadow-sm ${className}`}>
      <CardContent className="space-y-4 p-5">
        <div className="space-y-1">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <h3 className="text-base font-semibold text-slate-950">
                {deliveryEstimate.label || 'Estimated audience delivery'}
              </h3>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-700"
                  aria-label="Explain delivery estimate terms"
                >
                  <CircleHelp className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 rounded-2xl border-slate-200 bg-white p-4 shadow-lg">
                <PopoverHeader className="gap-2">
                  <PopoverTitle className="text-sm font-semibold text-slate-950">
                    Delivery estimate terms
                  </PopoverTitle>
                  <PopoverDescription className="text-xs leading-5 text-slate-600">
                    For marketers who want the precise terminology behind these estimate lines.
                  </PopoverDescription>
                </PopoverHeader>
                <div className="mt-4 space-y-3 text-sm text-slate-700">
                  <div>
                    <p className="font-medium text-slate-950">Ad views <span className="font-normal text-slate-500">(also called impressions)</span></p>
                    <p className="mt-1 leading-5">
                      The total number of times the ad is expected to be shown.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-slate-950">Reach</p>
                    <p className="mt-1 leading-5">
                      The estimated number of people expected to see the campaign at least once.
                    </p>
                  </div>
                  {deliveryEstimate.estimateBasis ? (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                      {deliveryEstimate.estimateBasis}
                    </div>
                  ) : null}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {deliveryEstimate.estimateBasis ? (
            <p className="text-sm text-slate-500">{deliveryEstimate.estimateBasis}</p>
          ) : null}
        </div>

        <div className="space-y-4">
          {impressionsRange ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                Estimated ad views
              </p>
              <p className="mt-1 text-2xl font-semibold text-slate-950">{impressionsRange}</p>
            </div>
          ) : null}

          {reachRange ? (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                Estimated people reached
              </p>
              <p className="mt-1 text-lg font-semibold text-slate-900">{reachRange}</p>
            </div>
          ) : null}
        </div>

        {deliveryEstimate.disclaimerText ? (
          <>
            <Separator />
            <p className="text-xs leading-5 text-slate-500">{deliveryEstimate.disclaimerText}</p>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
